import { haveSameUniqueElements } from '@utils/common.utils';

import {
  minimumRolesEnum,
  Role,
  RoleActionEnum,
  RoleEnum,
  roleMap,
  RoleOperationalDomainCategoryEnum,
  RoleTaskCategoryEnum,
} from '@/types/authent/roles.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';

export function isBasicRole(role: Role | RoleEnum) {
  if (typeof role === 'string') {
    return minimumRolesEnum.includes(role);
  }
  return minimumRolesEnum.includes(role.roleName);
}

export function isNotTechnical(role: Role | RoleEnum) {
  if (typeof role === 'string') {
    return role !== RoleEnum.ADMIN;
  }
  return (
    role.taskCategory !== RoleTaskCategoryEnum.APPLICATION &&
    role.taskCategory !== RoleTaskCategoryEnum.SUPER_ADMINISTRATION
  );
}

export function hasChangedCategoryRoles(
  initialRoles: RoleEnum[],
  currentRoles: RoleEnum[],
  category: keyof typeof roleMap,
) {
  const categoryRoles = Object.values(roleMap[category]).flat();
  const initialCategoryRoles = initialRoles.filter((role) => categoryRoles.includes(role));
  const currentCategoryRoles = currentRoles.filter((role) => categoryRoles.includes(role));
  return !haveSameUniqueElements(initialCategoryRoles, currentCategoryRoles);
}

export function getRolesByCategory(
  roles: Role[],
  category: RoleOperationalDomainCategoryEnum | RoleTaskCategoryEnum,
): RoleEnum[] {
  return roles
    .filter((role) => role.operationalDomainCategory === category || role.taskCategory === category)
    .map((role) => role.roleName);
}

export function getRoleAction(role: Role): string | null {
  return Object.values(RoleActionEnum).find((action) => role.roleName.startsWith(action)) ?? null;
}

export function getRoleNameAction(role: RoleEnum): ActionEnum | null {
  return Object.values(ActionEnum).find((action) => role.startsWith(action)) ?? null;
}

export function getRoleNameFamily(role: RoleEnum): SensorFamilyEnum | null {
  return Object.values(SensorFamilyEnum).find((family) => role.includes(`_${family}`)) ?? null;
}

export function isSensorRole(role: RoleEnum) {
  return getRoleNameAction(role) !== null && getRoleNameFamily(role) !== null;
}

export function filterRoles(allowed: RoleEnum[]): Record<string, Record<string, RoleEnum[]>> {
  const result: Record<string, Record<string, RoleEnum[]>> = {};

  for (const [category, subcats] of Object.entries(roleMap)) {
    const filteredSubcats: Record<string, RoleEnum[]> = {};

    for (const [subcat, roles] of Object.entries(subcats)) {
      const filteredRoles = roles.filter((r) => allowed.includes(r));

      if (filteredRoles.length > 0) {
        filteredSubcats[subcat] = filteredRoles;
      }
    }
    if (Object.keys(filteredSubcats).length > 0) {
      result[category] = filteredSubcats;
    }
  }

  return result;
}
